import { isAnalyticsEvent, trackEvent } from './analytics';
import { syncOfflinePlay } from './analyticsOffline';
import { displayMode } from './displayMode';

const SESSION_KEY = 'analytics_screen_session_v1';
let memorySessionSent = false;
let subscribers = 0;

function screenSession() {
  if (memorySessionSent) return;
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return;
  } catch {
    /* Storage may be disabled; a failed write below enables the fallback. */
  }
  const width = Math.round(window.innerWidth);
  const height = Math.round(
    window.visualViewport?.height ?? window.innerHeight,
  );
  trackEvent('screen_session_started', {
    display_mode: displayMode(),
    width,
    visible_height: height,
    dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    orientation: width > height ? 'landscape' : 'portrait',
    size_bucket:
      width < 360
        ? 'compact'
        : width < 768
          ? 'mobile'
          : width < 1024
            ? 'tablet'
            : 'desktop',
    small_screen: width < 360 || height < 560,
  });
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    memorySessionSent = true;
  }
}

function onClick(event: MouseEvent) {
  const target = event.target instanceof Element ? event.target : null;
  const element = target?.closest('[data-track]');
  if (
    !element ||
    element.closest(':disabled, [disabled], [aria-disabled="true"], [inert]')
  )
    return;
  const name = element.getAttribute('data-track') ?? '';
  if (!isAnalyticsEvent(name)) return;
  try {
    const raw = element.getAttribute('data-track-props');
    const data = raw ? JSON.parse(raw) : undefined;
    if (
      data !== undefined &&
      (!data || typeof data !== 'object' || Array.isArray(data))
    )
      return;
    trackEvent(name, data);
  } catch {
    /* Malformed annotations must not interrupt the CTA. */
  }
}

/** Reference counted for StrictMode and multiple roots; capture survives portals. */
export function installAnalyticsBaseline(): () => void {
  if (typeof window === 'undefined') return () => {};
  screenSession();
  if (subscribers++ === 0) {
    document.addEventListener('click', onClick, true);
    window.addEventListener('online', syncOfflinePlay);
    syncOfflinePlay();
  }
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    if (--subscribers === 0) {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('online', syncOfflinePlay);
    }
  };
}
